import styled from "@emotion/styled/macro";
import { type RefObject, useMemo, useState } from "react";
import {
  Button,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
  Tooltip,
  Typography,
} from "@sunrun/experience-ui-components";
import { ReviewOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { type PromotionSelectionItem } from "services/promotions";
import { CardCarouselData } from "providers/Types";
import { PriceOfPowerProspect } from "hooks/usePriceOfPowerUrl";
import { GraphContent } from "components/organisms/GraphContent";
import { ProductCarousel } from "components/molecules/ProductCarousel";
import {
  SimplifiedOfferReviewProductCarouselPanel,
  SimplifiedOfferReviewProductCarouselTabs,
  useSimplifiedProductCarousel,
} from "components/molecules/SimplifiedOfferReviewProductCarousel";
import {
  pickDesignImageUrl,
  useStorefrontDesignImages,
} from "hooks/useStorefrontDesignImages";
import bgNewHomes from "components/assets/images/bg-new-homes.jpg";
import { TreeRemovalLegend } from "components/molecules/TreeRemovalLegend";
import { OfferReviewSystemDesignDetailsModal } from "components/organisms/OfferReviewSystemDesignDetailsModal";

type SimplifiedOfferReviewContentProps = {
  offer: ReviewOffer;
  prospectId: string;
  prospect?: PriceOfPowerProspect;
  nonSolarView: boolean;
  hasBattery: boolean;
  isMobile: boolean;
  isCustomer: boolean;
  systemCarouselData: CardCarouselData[];
  showSolarStepsDetails: boolean;
  setShowSolarStepsDetails: (show: boolean) => void;
  onContactSalesRepClick: () => void;
  onCheckoutClick: () => void;
  setShowMapModalOpen: (show: boolean) => void;
  isTablet: boolean;
  isNewHomes?: boolean;
  showSelfSupply?: boolean;
  promotions?: PromotionSelectionItem[];
  showPromotions?: boolean;
  energyProfileGraphSelectNonce?: number;
  graphSectionRef?: RefObject<HTMLDivElement>;
};

type LayoutContentProps = SimplifiedOfferReviewContentProps;

const resolveWarrantyTooltip = (
  warranty: string,
  years: string
): string | null => {
  const warrantyText = warranty.toLowerCase();
  switch (warrantyText) {
    case "energy production guarantee":
      return "The system will produce [X]% or more of our production estimate, or we credit you for the diference.";
    case "workmanship warranty":
      return `Sunrun goes beyond what is ordered by the manufacturers and repairs or replaces the equipment as needed for ${years} years.`;
    case "equipment maintenance and repairs":
      return `If there's an issue with the installation or our work on the system, we'll fix it at no cost to you for ${years} years.`;
    case "battery replacement guarantee":
      return `If your battery stops working, we'll repair or replace it as necessary for ${years} years.`;
    case "roof penetration warranty":
      return `We guarantee that our roof penetrations are watertight for ${years} years.`;
    case "24/7 system monitoring":
      return `Our proactive system monitoring informs us of possible issues, so your system overview overview can operate at or above its Target Zone. With the Sunrun mobile app, you also have 24/7 access to your system overview overview's performance.`;
    case "award winning service and support":
      return `From a team of dedicated, on-call service agents and experienced technicians.`;
    case "guaranteed system transfer eligibility":
      return `If you move, we guarantee the next homeowner will qualify to assume the agreement.`;
    default:
      return null;
  }
};

type WarrantyListItemProps = {
  title: string;
  termYears: string;
  isMobile: boolean;
  isTablet: boolean;
};

const WarrantyListItem = ({
  title,
  termYears,
  isMobile,
  isTablet,
}: WarrantyListItemProps) => {
  const tooltipContent = resolveWarrantyTooltip(title, termYears);

  return (
    <WarrantyItem isMobile={isMobile}>
      <WarrantyItemContent>
        <SvgIcon
          name={SvgNames.SuccessCircle}
          width={24}
          height={24}
          color={tokens.BRAND_HERO_BLUE}
        />
        <Typography size={16} color={tokens.BRAND_HERO_BLUE}>
          {termYears} {termYears === "1" ? "year" : "years"} {title}
        </Typography>
      </WarrantyItemContent>
      {tooltipContent && (
        <WarrantyTooltip content={tooltipContent} direction={"top-right"}>
          <SvgIcon
            name={SvgNames.InfoCircle}
            width={14}
            height={14}
            color={tokens.BRAND_HERO_BLUE}
          />
        </WarrantyTooltip>
      )}
    </WarrantyItem>
  );
};

const SolarNewLayoutContent = ({
  offer,
  prospect,
  isMobile,
  isTablet,
  isNewHomes,
  setShowMapModalOpen,
  children,
}: LayoutContentProps & { children?: React.ReactNode }) => {
  const [showSystemDesignDetails, setShowSystemDesignDetails] = useState(false);
  const designImages = useStorefrontDesignImages(
    offer?.designId,
    offer?.designVersion
  );

  // The large render is the only one captured with trees shown, so it is the
  // version that lets the customer see which trees are marked for removal.
  const showTreeRemovalLegend = !isNewHomes && !!offer?.treeRemovalRequired;
  const siteModelImageUrl = showTreeRemovalLegend
    ? pickDesignImageUrl(
        designImages.data?.largeImageUrl,
        designImages.data?.smallImageUrl
      )
    : pickDesignImageUrl(
        designImages.data?.smallImageUrl,
        designImages.data?.largeImageUrl
      );
  const modalDesignImageUrl = pickDesignImageUrl(
    designImages.data?.largeImageUrl,
    designImages.data?.smallImageUrl,
    siteModelImageUrl
  );
  const previewDesignImageUrl = isNewHomes ? bgNewHomes : siteModelImageUrl;
  const modalImageUrl = isNewHomes ? bgNewHomes : modalDesignImageUrl;
  const hasDesignContext =
    Boolean(offer?.designId) && offer?.designVersion != null;
  const showDesignPreview =
    isNewHomes ||
    Boolean(previewDesignImageUrl) ||
    (hasDesignContext && designImages.isLoading);
  const previewBackgroundUrl = previewDesignImageUrl;
  const isDesignPreviewLoading =
    showDesignPreview && !previewBackgroundUrl && designImages.isLoading;
  const customerAddress =
    prospect?.customerStreet &&
    prospect?.customerCity &&
    prospect?.customerState &&
    prospect?.customerZipCode
      ? `${prospect.customerStreet}, ${prospect.customerCity}, ${prospect.customerState} ${prospect.customerZipCode}`
      : undefined;

  const equipmentProducts = useMemo(
    () => [...offer.includedProducts, ...offer.accessoryProducts],
    [offer.includedProducts, offer.accessoryProducts]
  );
  const {
    sortedProducts: carouselProducts,
    currentIndex: carouselIndex,
    setCurrentIndex: setCarouselIndex,
  } = useSimplifiedProductCarousel(equipmentProducts);

  return (
    <NewContentWrapper isMobile={isMobile}>
      <SystemOverview isMobile={isMobile}>
        <Container>
          <SystemOverviewContent
            isMobile={isMobile}
            isTablet={isTablet}
            alignStart
          >
            <DesignEquipmentLeftColumn>
              <SectionTitle
                size={isMobile ? 20 : 28}
                color={tokens.BRAND_HERO_BLUE}
              >
                Design and equipment
              </SectionTitle>
              {showDesignPreview && (
                <SolarDesignImageWrapper>
                  {isNewHomes && (
                    <NewHomesDisclaimer
                      size={12}
                      color={tokens.BRAND_MEDIUM_BLUE}
                    >
                      This is a representative image and not your actual home
                    </NewHomesDisclaimer>
                  )}
                  <SolarDesignImage
                    bgImageUrl={previewBackgroundUrl}
                    $isLoading={isDesignPreviewLoading}
                    data-testid="offer-review-solar-design-image"
                  >
                    <DesignExpandButton
                      size="sm"
                      color="secondary"
                      aria-label="View solar design"
                      data-testid="offer-review-solar-design-expand"
                      $isNewHomes={isNewHomes}
                      onClick={() => setShowMapModalOpen(true)}
                      iconMiddle={
                        <SvgIcon
                          name={SvgNames.ArrowsOutSimple}
                          width={18}
                          height={18}
                          color={tokens.BRAND_HERO_BLUE}
                        />
                      }
                    />
                  </SolarDesignImage>
                  {showTreeRemovalLegend && <OverlaidTreeRemovalLegend />}
                </SolarDesignImageWrapper>
              )}
            </DesignEquipmentLeftColumn>
            <DesignEquipmentRightColumn>
              <SimplifiedOfferReviewProductCarouselTabs
                sortedProducts={carouselProducts}
                currentIndex={carouselIndex}
                onSelect={setCarouselIndex}
              />
              <SimplifiedOfferReviewProductCarouselPanel
                sortedProducts={carouselProducts}
                currentIndex={carouselIndex}
                onIndexChange={setCarouselIndex}
              />
              <LinkRow>
                <LinkButton
                  color="link"
                  size="sm"
                  data-testid="offer-review-see-specifications"
                  onClick={() => setShowSystemDesignDetails(true)}
                >
                  View specs
                </LinkButton>
                <LinkButton
                  color="link"
                  size="sm"
                  data-testid="offer-review-how-solar-storage-works"
                  onClick={() =>
                    window.open(
                      "https://s.tiled.co/2ScEQV5/sales-proposal-23-non-shift",
                      "_blank"
                    )
                  }
                >
                  How solar+storage works
                </LinkButton>
              </LinkRow>
            </DesignEquipmentRightColumn>
          </SystemOverviewContent>
          {showSystemDesignDetails && (
            <OfferReviewSystemDesignDetailsModal
              offer={offer}
              designImageUrl={modalImageUrl}
              address={customerAddress}
              onClose={() => setShowSystemDesignDetails(false)}
            />
          )}
        </Container>
        {children}
        {offer?.financial?.summary?.warranties?.length > 0 && (
          <Warranties isMobile={isMobile}>
            <Typography
              size={isMobile ? 32 : 44}
              color={tokens.BRAND_HERO_BLUE}
            >
              Your Sunrun guarantee
              <StyledPeaceOfMindText>Have peace of mind</StyledPeaceOfMindText>
            </Typography>

            <WarrantiesList isMobile={isMobile}>
              {offer.financial.summary.warranties.map((el, index) => (
                <WarrantyListItem
                  key={`${el.title}-${el.termYears}-${index}`}
                  title={el.title}
                  termYears={el.termYears}
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              ))}
            </WarrantiesList>
          </Warranties>
        )}
      </SystemOverview>
    </NewContentWrapper>
  );
};

const NonSolarNewLayoutContent = ({
  offer,
  hasBattery,
  isMobile,
  isTablet,
  systemCarouselData,
  promotions,
  showPromotions,
}: LayoutContentProps) => (
  <NewContentWrapper isMobile={isMobile}>
    <SystemOverview isMobile={isMobile}>
      <Container>
        <ContentTitle size={isMobile ? 20 : 28} color={tokens.BRAND_HERO_BLUE}>
          Equipment
        </ContentTitle>
        <SystemOverviewContent
          isMobile={isMobile}
          isTablet={isTablet}
          singleColumn={true}
        >
          <ProductCarousel
            products={[...offer.includedProducts, ...offer.accessoryProducts]}
            offer={offer}
          />
        </SystemOverviewContent>
      </Container>
    </SystemOverview>
    <Warranties isMobile={isMobile}>
      <Typography size={isMobile ? 32 : 44} color={tokens.BRAND_HERO_BLUE}>
        Your Sunrun guarantee
        <StyledPeaceOfMindText>Have peace of mind</StyledPeaceOfMindText>
      </Typography>
      <WarrantiesList isMobile={isMobile}>
        {offer.financial.summary.warranties.map((el, index) => (
          <WarrantyListItem
            key={`${el.title}-${el.termYears}-${index}`}
            title={el.title}
            termYears={el.termYears}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        ))}
      </WarrantiesList>
    </Warranties>
  </NewContentWrapper>
);

const SimplifiedOfferReviewContent = (
  props: SimplifiedOfferReviewContentProps
) => {
  const {
    isNewHomes,
    energyProfileGraphSelectNonce,
    graphSectionRef,
    ...rest
  } = props;
  return rest.nonSolarView ? (
    <NonSolarNewLayoutContent {...rest} />
  ) : (
    <SolarNewLayoutContent isNewHomes={isNewHomes} {...rest}>
      {!isNewHomes && (
        <GraphSectionAnchor ref={graphSectionRef}>
          <GraphContent
            offer={rest.offer}
            prospect={rest.prospect}
            testId="Review-Page-Graph-Section"
            showSelfSupply={rest.showSelfSupply}
            graphTabOrder="targetZoneFirst"
            energyProfileSelectNonce={energyProfileGraphSelectNonce}
          />
        </GraphSectionAnchor>
      )}
    </SolarNewLayoutContent>
  );
};

const GraphSectionAnchor = styled.div`
  width: 100%;
`;

const NewContentWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ isMobile }) => (isMobile ? "24px 16px" : "48px 64px")};
`;

const SystemOverview = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "24px" : "48px")};
`;

const SystemOverviewContent = styled.div<{
  isMobile: boolean;
  isTablet: boolean;
  singleColumn?: boolean;
  alignStart?: boolean;
}>`
  display: flex;
  flex-direction: ${({ isTablet, isMobile }) =>
    isTablet || isMobile ? "column" : "row"};
  gap: ${({ singleColumn }) => (singleColumn ? "64px" : "24px")};
  align-items: ${({ singleColumn, alignStart }) =>
    alignStart || singleColumn ? "flex-start" : "center"};
`;

const DesignEquipmentLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    width: 100%;
    align-self: stretch;
  }
`;

const DesignEquipmentRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const SolarDesignImageWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 608px;
  height: 440px;
  border-radius: 10px;
  overflow: visible;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex: none;
    width: 100%;
    max-width: none;
    min-width: 0;

    /* Fluid height: 336px at 375px viewport → 440px at 768px viewport */
    --carousel-height-min: 336px;
    --carousel-height-max: 440px;
    --carousel-vw-min: 375px;
    --carousel-vw-range: 393px; /* 768 - 375 */
    --carousel-height-range: 104px; /* 440 - 336 */
    height: clamp(
      var(--carousel-height-min),
      calc(
        var(--carousel-height-min) + (100vw - var(--carousel-vw-min)) *
          var(--carousel-height-range) / var(--carousel-vw-range)
      ),
      var(--carousel-height-max)
    );
  }
`;

const SolarDesignImage = styled.div<{
  bgImageUrl?: string;
  $isLoading?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: ${({ bgImageUrl, $isLoading }) =>
    bgImageUrl
      ? `url(${bgImageUrl}) no-repeat center`
      : $isLoading
      ? `linear-gradient(
            90deg,
            color-mix(in srgb, ${tokens.BRAND_MEDIUM_BLUE} 12%, ${tokens.WHITE}) 0%,
            color-mix(in srgb, ${tokens.BRAND_MEDIUM_BLUE} 22%, ${tokens.WHITE}) 50%,
            color-mix(in srgb, ${tokens.BRAND_MEDIUM_BLUE} 12%, ${tokens.WHITE}) 100%
          )`
      : `color-mix(in srgb, ${tokens.BRAND_MEDIUM_BLUE} 10%, ${tokens.WHITE})`};
  background-size: ${({ bgImageUrl }) => (bgImageUrl ? "cover" : "200% 100%")};
  animation: ${({ $isLoading, bgImageUrl }) =>
    $isLoading && !bgImageUrl
      ? "design-preview-shimmer 1.2s ease-in-out infinite"
      : "none"};

  @keyframes design-preview-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const DesignExpandButton = styled(Button)<{ $isNewHomes?: boolean }>`
  position: absolute;
  bottom: 14px;
  right: 14px;
  z-index: 1;
  width: 34px;
  height: 34px;
  box-shadow: 0 2px 8px color-mix(in srgb, ${tokens.BLACK} 15%, transparent);
  visibility: ${({ $isNewHomes }) => ($isNewHomes ? "hidden" : "visible")};

  &.sr-button-secondary {
    background: ${tokens.WHITE};
    border: none;
    border-radius: 50%;
  }
`;

const LinkRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: fit-content;
  margin-left: auto;
`;

const LinkButton = styled(Button)`
  align-self: flex-end;
  margin-left: auto;
  font-size: 14px;
`;

const OverlaidTreeRemovalLegend = styled(TreeRemovalLegend)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const SectionTitle = styled(Typography)`
  line-height: 110% !important;
  letter-spacing: -0.56px;
`;

const ContentTitle = styled(Typography)`
  line-height: 110% !important;
  letter-spacing: -0.56px;
  text-decoration: underline;
`;

const WarrantyTooltip = styled(Tooltip)`
  .sr-tooltip-content,
  .sr-tooltip-content-mobile {
    min-width: 240px;
    max-width: 320px;
    padding: 16px;
    font-size: 12px;
    line-height: 1.4;

    @media (min-width: ${TABLET_BREAKPOINT}px) {
      padding: 24px;
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const Warranties = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "16px" : "24px")};
  margin-bottom: ${({ isMobile }) => (isMobile ? "32px" : "0")};
`;

const StyledPeaceOfMindText = styled(Typography)`
  && {
    opacity: 0.7;
    font-size: 24px;
  }
`;

const WarrantiesList = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 0;
  grid-column-gap: 24px;

  ${({ isMobile }) =>
    isMobile &&
    `
  display: flex;
  flex-direction: column;
  gap: 4px;
`}
`;

const WarrantyItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${({ isMobile }) => (isMobile ? "16px 0" : "16px")};
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
`;

const WarrantyItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const NewHomesDisclaimer = styled(Typography)`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 16px;
`;

export { SimplifiedOfferReviewContent };
